import { assertImage, getRgb } from './image.js';
const OWL_WIDTH = 434;
const OWL_HEIGHT = 317;
const TEMPLATE_WIDTH = 8;
const TEMPLATE_HEIGHT = 16;
const TEMPLATE_Y_OFFSET_FROM_WINDOW_TOP = 68;
const RIGHT_TEMPLATE_X_OFFSET_FROM_WINDOW_LEFT = 426;
function rgb(r, g, b) {
    return [r, g, b];
}
/** RGB version of the notebook's BGR cv2 template. */
export function makeOwlSideTemplate(mirrored = false) {
    const t = Array.from({ length: TEMPLATE_HEIGHT }, () => Array.from({ length: TEMPLATE_WIDTH }, () => rgb(255, 255, 255)));
    const setRows = (start, end, color) => {
        for (let y = start; y < end; y++) {
            for (let x = 0; x < TEMPLATE_WIDTH; x++)
                t[y][x] = color;
        }
    };
    setRows(0, 4, rgb(0x99, 0xbb, 0xcc));
    setRows(4, 7, rgb(0xbb, 0xcc, 0xdd));
    setRows(5, 6, rgb(0xcc, 0xdd, 0xdd));
    setRows(7, 8, rgb(0x55, 0x77, 0x99));
    setRows(8, TEMPLATE_HEIGHT, rgb(0xff, 0xff, 0xff));
    for (let y = 0; y < TEMPLATE_HEIGHT; y++)
        t[y][0] = rgb(0x55, 0x77, 0x99);
    if (!mirrored)
        return t;
    return t.map((row) => [...row].reverse());
}
function channelStats(template) {
    let sum = 0;
    let count = 0;
    for (const row of template) {
        for (const p of row) {
            sum += p[0] + p[1] + p[2];
            count += 3;
        }
    }
    const mean = sum / count;
    let ss = 0;
    for (const row of template) {
        for (const p of row) {
            ss += (p[0] - mean) ** 2 + (p[1] - mean) ** 2 + (p[2] - mean) ** 2;
        }
    }
    return { mean, variance: ss };
}
function normalizedCorrelationAt(image, template, templateStats, x0, y0) {
    const count = TEMPLATE_WIDTH * TEMPLATE_HEIGHT * 3;
    let imageSum = 0;
    for (let y = 0; y < TEMPLATE_HEIGHT; y++) {
        for (let x = 0; x < TEMPLATE_WIDTH; x++) {
            const p = getRgb(image, x0 + x, y0 + y);
            imageSum += p[0] + p[1] + p[2];
        }
    }
    const imageMean = imageSum / count;
    let numerator = 0;
    let imageVariance = 0;
    for (let y = 0; y < TEMPLATE_HEIGHT; y++) {
        for (let x = 0; x < TEMPLATE_WIDTH; x++) {
            const img = getRgb(image, x0 + x, y0 + y);
            const tm = template[y][x];
            for (let c = 0; c < 3; c++) {
                const a = img[c] - imageMean;
                const b = tm[c] - templateStats.mean;
                numerator += a * b;
                imageVariance += a * a;
            }
        }
    }
    if (imageVariance === 0 || templateStats.variance === 0)
        return 0;
    return numerator / Math.sqrt(imageVariance * templateStats.variance);
}
function rgbClose(a, b, tolerance = 24) {
    return Math.abs(a[0] - b[0]) <= tolerance
        && Math.abs(a[1] - b[1]) <= tolerance
        && Math.abs(a[2] - b[2]) <= tolerance;
}
function roughTemplateMatch(image, template, x0, y0) {
    // Enough distinctive points to avoid running normalized correlation across the whole screenshot.
    // Tolerance handles the demo fixture's few +17 red-channel pixels around the cursor/edge.
    const points = [
        [0, 0], [1, 0], [0, 7], [1, 7], [1, 8], [7, 15],
    ];
    for (const [x, y] of points) {
        if (!rgbClose(getRgb(image, x0 + x, y0 + y), template[y][x]))
            return false;
    }
    return true;
}
function findBestTemplateMatch(image, template, step) {
    const stats = channelStats(template);
    let best = { x: -1, y: -1, score: -Infinity };
    const maxY = image.height - TEMPLATE_HEIGHT;
    const maxX = image.width - TEMPLATE_WIDTH;
    let candidates = 0;
    for (let y = 0; y <= maxY; y += step) {
        for (let x = 0; x <= maxX; x += step) {
            if (!roughTemplateMatch(image, template, x, y))
                continue;
            candidates++;
            const score = normalizedCorrelationAt(image, template, stats, x, y);
            if (score > best.score)
                best = { x, y, score };
        }
    }
    // Unusual palette/theme? Fall back to full normalized correlation so callers can lower threshold.
    if (candidates === 0) {
        for (let y = 0; y <= maxY; y += step) {
            for (let x = 0; x <= maxX; x += step) {
                const score = normalizedCorrelationAt(image, template, stats, x, y);
                if (score > best.score)
                    best = { x, y, score };
            }
        }
    }
    return best;
}
/** Detect the only supported Owl window: the canonical 434×317 result region. */
export function detectOwlWindow(image, options = {}) {
    assertImage(image);
    const threshold = options.threshold ?? 0.99;
    const step = options.step ?? 1;
    if (!Number.isInteger(step) || step <= 0)
        throw new Error(`Invalid template search step: ${step}`);
    const left = findBestTemplateMatch(image, makeOwlSideTemplate(false), step);
    const right = findBestTemplateMatch(image, makeOwlSideTemplate(true), step);
    const leftDetection = {
        matchedSide: 'left',
        score: left.score,
        anchor: { x: left.x, y: left.y },
        rect: {
            x: left.x,
            y: left.y - TEMPLATE_Y_OFFSET_FROM_WINDOW_TOP,
            width: OWL_WIDTH,
            height: OWL_HEIGHT,
        },
    };
    const rightDetection = {
        matchedSide: 'right',
        score: right.score,
        anchor: { x: right.x, y: right.y },
        rect: {
            x: right.x - RIGHT_TEMPLATE_X_OFFSET_FROM_WINDOW_LEFT,
            y: right.y - TEMPLATE_Y_OFFSET_FROM_WINDOW_TOP,
            width: OWL_WIDTH,
            height: OWL_HEIGHT,
        },
    };
    const best = leftDetection.score >= rightDetection.score ? leftDetection : rightDetection;
    if (best.score < threshold)
        return null;
    if (best.rect.x < 0
        || best.rect.y < 0
        || best.rect.x + best.rect.width > image.width
        || best.rect.y + best.rect.height > image.height)
        return null;
    return best;
}
//# sourceMappingURL=cropper.js.map